import { useContext, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useMutation } from '@tanstack/react-query';
import { Button, Modal, TextFieldFormik } from '@components';
import { ScreenSizeContext } from '@context';
import { DqpAccountService } from '@data';
import { Close, Done } from '@public';
import { DqpRegistrationPayload } from '@types';
import { aussiePhoneRegExp, cmiNumberRegExp, emailRegExp, FormLabels, useAutoLogout } from '@utils';
import { Form, Formik, FormikProps } from 'formik';
import * as Yup from 'yup';

import ownStyles from './dqpRegistration.module.scss';
import styles from '@pages/registration.module.scss';

interface DqpRegistrationFormValues {
	cmiNumber: string;
	email: string;
	mobile: string;
}

const dqpRegistrationFormLabels: FormLabels<DqpRegistrationFormValues> = {
	cmiNumber: 'CMI number',
	email: 'Work email address',
	mobile: 'Work mobile number',
};

const dqpRegistrationSchema = Yup.object().shape({
	cmiNumber: Yup.string()
		.matches(cmiNumberRegExp, `${dqpRegistrationFormLabels.cmiNumber} is not valid`)
		.required(`${dqpRegistrationFormLabels.cmiNumber} is required`),
	email: Yup.string()
		.matches(emailRegExp, `${dqpRegistrationFormLabels.email} is not valid`)
		.required(`${dqpRegistrationFormLabels.email} is required`),
	mobile: Yup.string()
		.matches(aussiePhoneRegExp, `${dqpRegistrationFormLabels.mobile} is not valid`)
		.required(`${dqpRegistrationFormLabels.mobile} is required`),
});

export interface DqpRegistrationProps {
	isForAdditional?: boolean;
	onClickBack?: () => void;
}

export const DqpRegistration = ({
	onClickBack = () => {
		return;
	},
}: DqpRegistrationProps) => {
	const router = useRouter();

	// Context
	const { isSmallDT, isMobile } = useContext(ScreenSizeContext);

	// State
	const [initialValues, setInitialValues] = useState<DqpRegistrationFormValues>({
		cmiNumber: '',
		email: '',
		mobile: '',
	});
	const [isTryingAgain, setIsTryingAgain] = useState(false);

	// Others
	const autoLogout = useAutoLogout();

	// Data
	const registerDqpAccount = useMutation({
		mutationFn: (payload: DqpRegistrationPayload) => DqpAccountService.registerDqpAccount(payload),
	});

	const onSubmit = async (values: DqpRegistrationFormValues) => {
		setInitialValues(values);
		const data = castFormValues(values);
		await registerDqpAccount.mutateAsync(data).then(() => autoLogout());
	};

	const castFormValues = (values: DqpRegistrationFormValues): DqpRegistrationPayload => {
		return {
			CMIId: values.cmiNumber,
			email: values.email,
			mobile: values.mobile,
		};
	};

	const navigateToContact = () => {
		router.push('/contact');
	};

	const MainContent = () => (
		<>
			<h2 className={styles.title} data-testid="pageTitle">
				WaterIQ registration: Duly Qualified Person
			</h2>
			<p>
				To complete your DQP verification, please provide further information. Please note your
				registration request will be manually processed by a Water Officer and will take up to 3-5
				working days. Where inaccurate data has been provided, this may take longer. Following
				submission you will receive an email to confirm your registration request. Once your request
				has been actioned, you will receive an additional follow up email to confirm your
				registration with WaterIQ.
			</p>

			<Formik
				initialValues={initialValues}
				validationSchema={dqpRegistrationSchema}
				onSubmit={onSubmit}
			>
				{({ dirty, submitForm }: FormikProps<DqpRegistrationFormValues>) => (
					<Form>
						<div className={ownStyles['form-container']}>
							<div
								className={[
									ownStyles.grid,
									registerDqpAccount.isPending ? ownStyles['grid--frozen'] : '',
								].join(' ')}
							>
								{/* CMI number */}
								<>
									<label>{dqpRegistrationFormLabels.cmiNumber}:</label>
									<TextFieldFormik
										props={{
											name: 'cmiNumber',
										}}
										size="tiny"
										testId="cmiNumber"
									/>
								</>

								{/* Work email address */}
								<>
									<label>{dqpRegistrationFormLabels.email}:</label>
									<TextFieldFormik
										props={{
											name: 'email',
										}}
										size="auto"
										testId="email"
									/>
								</>

								{/* Work mobile number */}
								<>
									<label>{dqpRegistrationFormLabels.mobile}:</label>
									<TextFieldFormik
										props={{
											name: 'mobile',
										}}
										size="tiny"
										testId="mobile"
									/>
								</>
							</div>
							<div className={ownStyles['buttons-container']}>
								<Button
									key="cancel"
									type="secondary"
									size="auto"
									onClick={onClickBack}
									disabled={registerDqpAccount.isPending}
									data-testid="cancel"
								>
									Cancel
								</Button>
								<Button
									size="auto"
									type="primary"
									onClick={submitForm}
									isLoading={registerDqpAccount.isPending}
									disabled={!dirty && !isTryingAgain}
									data-testid="submit"
								>
									Register for WaterIQ as Duly Qualified Person
								</Button>
							</div>
						</div>
					</Form>
				)}
			</Formik>
		</>
	);

	const SuccessContent = () => (
		<div className={styles['content-success']}>
			<div className={styles['title-row']}>
				<Done customStyles={[styles.icon, styles['icon-success']].join(' ')} />
				<h2 className={[styles.title, styles['title-success']].join(' ')} data-testid="pageTitle">
					Submission successful
				</h2>
			</div>
			<div className={styles['row-content']}>
				<p>
					Thank you for submitting your registration request with WaterIQ. Your request has been
					successfully received. You have been sent a confirmation email with details of your
					registration request.
				</p>
			</div>
			<div className={styles['row-content']}>
				<p>Please note you will now be logged out.</p>
			</div>
		</div>
	);

	const FailureModal = () => {
		return (
			<Modal customModalStyle={styles.modal} open={registerDqpAccount.isError}>
				<>
					<div className={styles['row-content']}>
						<Close customStyles={[styles.icon, styles['icon-error']].join(' ')} />
						<div>
							<p>Details provided were unable to be verified.</p>
						</div>
					</div>
					<div className={styles['button-modal-row']}>
						<Button
							size="small"
							type="primary"
							onClick={() => {
								setIsTryingAgain(true);
								registerDqpAccount.reset();
							}}
							data-testid={'tryAgain'}
						>
							Try again
						</Button>
						<Button
							customStyles={[styles['button-contact']].join(' ')}
							size="large"
							type="secondary"
							onClick={navigateToContact}
							data-testid={'contactUs'}
						>
							{isSmallDT || isMobile ? 'Contact us' : 'Contact us for assistance'}
						</Button>
					</div>
				</>
			</Modal>
		);
	};

	return registerDqpAccount.isIdle || registerDqpAccount.isPending
		? MainContent()
		: registerDqpAccount.isSuccess
			? SuccessContent()
			: FailureModal();
};
